import React from 'react'
import { Text, Link } from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { DayforceBookings } from 'api/admin/dayforce/listDayforceBookings'
import { useDayforceBookingsContextValue } from '../context/DayforceBookingsProvider'
import { useDayforceAgenciesContextValue } from 'pages/Dayforce/shared/context/DayforceAgenciesProvider'
import * as ROUTES from 'constants/routes'
import { formatInTimeZone } from 'date-fns-tz'
import { DAYFORCE_SHIFTS_DEFAULT_TIMEZONE } from 'pages/Dayforce/constants'
import { PaginatedTable } from 'components/Table/PaginatedTable'

const AgencyCell = ({ agencyId }: { agencyId: string }) => {
  const {
    state: { agencies },
  } = useDayforceAgenciesContextValue()

  return (
    <Text style={{ whiteSpace: 'nowrap' }}>
      {agencies?.[agencyId]?.name || agencyId}
    </Text>
  )
}

const JobCell = ({ agencyId, jobId }: { agencyId: string; jobId: string }) => {
  return (
    <Link
      to={ROUTES.DAYFORCE_JOB_DETAIL.replace(':agencyId', agencyId).replace(
        ':jobId',
        jobId
      )}
      target="_blank"
    >
      {jobId}
    </Link>
  )
}

const ShiftCell = ({ shiftId }: { shiftId: number | null }) => {
  if (!shiftId) {
    return <Text>Shift not linked</Text>
  }

  return (
    <Link
      to={ROUTES.SHIFT_DETAIL.replace(':shiftId', shiftId.toString())}
      target="_blank"
    >
      {shiftId}
    </Link>
  )
}

const CompanyCell = ({
  companyId,
  companyName,
}: {
  companyId: number | null
  companyName: string | null
}) => {
  if (!companyId) {
    return <Text>Company not linked</Text>
  }

  return (
    <Link
      to={ROUTES.COMPANY_DETAIL.replace(':companyId', companyId.toString())}
      target="_blank"
    >
      {companyName} ({companyId})
    </Link>
  )
}

const TimeCell = ({ date }: { date: string }) => {
  const formattedDate = formatInTimeZone(
    date,
    DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
    'MMM, dd, yyyy, hh:mm a'
  )

  return <Text>{formattedDate}</Text>
}

const columns: ColumnDef<DayforceBookings[number]>[] = [
  {
    header: 'Agency',
    accessorKey: 'agencyId',
    cell: ({ row }) => <AgencyCell agencyId={row.original.agencyId} />,
  },
  {
    header: 'Job ID',
    accessorKey: 'dayforceJobId',
    cell: ({ row }) => (
      <JobCell
        agencyId={row.original.agencyId}
        jobId={row.original.dayforceJobId}
      />
    ),
  },
  {
    header: 'Job Name',
    accessorKey: 'jobName',
  },
  {
    header: 'Job Group ID',
    accessorKey: 'dayforceJobGroupId',
  },
  {
    header: 'Workers Required',
    accessorKey: 'workersRequired',
  },
  {
    header: 'Backup Workers Required',
    accessorKey: 'backupWorkersRequired',
  },
  {
    header: 'Starts At',
    accessorKey: 'startsAt',
    cell: ({ row }) => <TimeCell date={row.original.startsAt} />,
  },
  {
    header: 'Finishes At',
    accessorKey: 'finishesAt',
    cell: ({ row }) => <TimeCell date={row.original.finishesAt} />,
  },
  {
    header: 'Position Name',
    accessorKey: 'positionName',
  },
  {
    header: 'Level',
    accessorKey: 'level',
  },
  {
    header: 'Organization Name',
    accessorKey: 'organizationName',
  },
  {
    header: 'Shift ID',
    accessorKey: 'shiftId',
    cell: ({ row }) => <ShiftCell shiftId={row.original.shiftId} />,
  },
  {
    header: 'Company',
    accessorKey: 'companyId',
    cell: ({ row }) => (
      <CompanyCell
        companyId={row.original.companyId}
        companyName={row.original.companyName}
      />
    ),
  },
]

export const DayforceBookingsTable = () => {
  const {
    state: { data, pagination, isFetching },
    actions: { setPagination },
  } = useDayforceBookingsContextValue()

  if (!data) {
    return <Text>No data found.</Text>
  }

  return (
    <PaginatedTable
      columns={columns}
      items={data.items}
      rowCount={data.totalRows}
      pagination={pagination}
      setPagination={setPagination}
      isFetching={isFetching}
      emptyMessage="No data found."
    />
  )
}
